// External imports
import React from 'react';
// Internal imports
import { ROLE_TYPE } from '~/app/constants';
import ErrorHelpers from '~/app/errorHelpers.js';
import WebHelpers from '~/app/webHelpers.js';
import { actions as appActions } from '~/app/redux/modules/app';
import {
  SSO_DETAIL_TEMPLATES,
  SSO_LOGIN_TYPE_LABELS,
  SSO_MFA_REASON,
  SSO_NO_ACCOUNT_COPY_BY_REASON,
  SSO_NO_ACCOUNT_DEFAULT_COPY,
  SSO_NON_EXPANDABLE_ROLE_TYPES,
  SSO_PRE_AUTH_TOKEN_KEY,
  SSO_ROLE_BUNDLE,
} from '~/app/Pages/SSOAccountPicker/helpers/ssoConstants.js';

/**
 * Extracts a numeric role type ID from supported account shape variants.
 *
 * @param {Object} account SSO account row.
 * @returns {number|null} Parsed role type ID, or null when unavailable.
 */
export const getRoleTypeID = account => {
  // Some rows include multiple role type fields and textual values may appear
  // before a valid numeric hierarchy_type_id. Select the first numerically
  // valid candidate rather than the first non-empty value.
  const roleTypeCandidates = [account.role_type_id, account.role_type, account.hierarchy_type_id];
  const validTypeID = roleTypeCandidates.find(candidate => {
    if (candidate == null) {
      return false;
    }

    const value = typeof candidate === 'string' ? candidate.trim() : candidate;
    if (value === '') {
      return false;
    }

    return !Number.isNaN(Number(value));
  });

  if (validTypeID == null) return null;

  const typeID = Number(validTypeID);
  return Number.isNaN(typeID) ? null : typeID;
};

/**
 * Maps a role type ID to a detail bundle used by the quick-details panel.
 *
 * @param {number|null} roleTypeID Role type ID derived from account data.
 * @returns {string|null} Bundle key from SSO_ROLE_BUNDLE, or null when unknown.
 */
export const getRoleBundle = roleTypeID => {
  switch (roleTypeID) {
    case ROLE_TYPE.SUPERUSER:
      return SSO_ROLE_BUNDLE.SUPERUSER;
    case ROLE_TYPE.RESELLER:
    case ROLE_TYPE.MULTIOFFICE:
      return SSO_ROLE_BUNDLE.RESELLER_MULTIOFFICE;
    case ROLE_TYPE.EFIN:
    case ROLE_TYPE.PREPARER:
      return SSO_ROLE_BUNDLE.EFIN_PREPARER;
    case ROLE_TYPE.TECH_SUPPORT:
    case ROLE_TYPE.TECH_SUPPORT_SBA:
      return SSO_ROLE_BUNDLE.TECH_SUPPORT;
    default:
      return null;
  }
};

/**
 * Resolves the display label for the account role.
 *
 * @param {Object} account SSO account row.
 * @returns {string} Human-readable role label.
 */
export const getRoleDisplayLabel = account => {
  const roleTypeID = getRoleTypeID(account);
  return SSO_LOGIN_TYPE_LABELS[roleTypeID] || account.role || `Unknown (${roleTypeID ?? 'N/A'})`;
};

/**
 * Indicates whether the row supports expandable quick details.
 *
 * @param {Object} account SSO account row.
 * @returns {boolean} True when details are expandable for the role type.
 */
export const isDetailsExpandable = account => {
  const bundle = getRoleBundle(getRoleTypeID(account));
  return Boolean(bundle && SSO_DETAIL_TEMPLATES[bundle]);
};

/**
 * Formats the Office/Company cell value, returning N/A for non-expandable roles.
 *
 * @param {Object} account SSO account row.
 * @returns {string} Office display value.
 */
export const getOfficeCompanyDisplay = account => {
  const roleTypeID = getRoleTypeID(account);
  if (SSO_NON_EXPANDABLE_ROLE_TYPES.includes(roleTypeID)) {
    return 'N/A';
  }

  const office = (account.office || '').trim();
  return office || 'N/A';
};

/**
 * Normalizes the last-used value for display in the picker table.
 *
 * @param {Object} account SSO account row.
 * @returns {string} Last-used display value, or N/A when unavailable.
 */
export const getLastUsedDisplay = account => {
  const raw = account.last_used;

  if (raw == null) {
    return 'N/A';
  }

  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? 'N/A' : raw.toISOString().slice(0, 10);
  }

  const value = String(raw).trim();
  if (!value || value.toLowerCase() === 'null') {
    return 'N/A';
  }

  if (value.startsWith('0001-01-01') || value.startsWith('0000-00-00')) {
    return 'N/A';
  }

  return value;
};

/**
 * Normalizes generic scalar values for table-cell display.
 *
 * @param {unknown} value Raw value from API.
 * @returns {string} Display-friendly value or N/A.
 */
export const getCellDisplay = value => {
  if (value == null) {
    return 'N/A';
  }

  const normalized = String(value).trim();
  return normalized || 'N/A';
};

/**
 * Converts raw values into trimmed strings for deterministic sorting.
 *
 * @param {unknown} value Raw sortable value.
 * @returns {string} Normalized string representation.
 */
export const toComparableSortValue = value => String(value ?? '').trim();

/**
 * True when value should be treated as missing during sort.
 *
 * @param {unknown} value Raw sortable value.
 * @returns {boolean} Whether the value is a placeholder/missing token.
 */
export const isMissingSortValue = value => {
  const normalized = toComparableSortValue(value).toLowerCase();
  return (
    normalized === '' ||
    normalized === 'n/a' ||
    normalized === 'na' ||
    normalized === 'null' ||
    normalized === 'undefined'
  );
};

/**
 * Compares text values while always keeping placeholder values at the bottom.
 *
 * @param {unknown} left Left sortable value.
 * @param {unknown} right Right sortable value.
 * @param {boolean} isSortAsc True for ascending order.
 * @returns {number} Sort comparator result.
 */
export const compareTextSortValues = (left, right, isSortAsc) => {
  const leftMissing = isMissingSortValue(left);
  const rightMissing = isMissingSortValue(right);

  if (leftMissing && rightMissing) return 0;
  if (leftMissing) return 1;
  if (rightMissing) return -1;

  const comparison = toComparableSortValue(left).localeCompare(
    toComparableSortValue(right),
    undefined,
    {
      numeric: true,
      sensitivity: 'base',
    },
  );

  return isSortAsc ? comparison : -comparison;
};

/**
 * Parses picker last-used values into timestamps for date sorting.
 *
 * @param {unknown} value Last-used field value.
 * @returns {number|null} Timestamp in ms, or null when value is missing/invalid.
 */
export const toLastUsedTimestamp = value => {
  const normalized = toComparableSortValue(value).toLowerCase();
  if (!normalized || normalized === 'n/a') {
    return null;
  }

  const time = Date.parse(normalized);
  return Number.isNaN(time) ? null : time;
};

/**
 * Builds quick-detail label/value pairs for the selected account row.
 *
 * @param {Object} account SSO account row.
 * @returns {Array<{label: string, value: any}>} Quick-detail items for rendering.
 */
export const getQuickDetailsForAccount = account => {
  const roleTypeID = getRoleTypeID(account);
  const bundle = getRoleBundle(roleTypeID);

  // Non-expandable roles (superUser, techSupport) have no detail template
  if (!bundle || !SSO_DETAIL_TEMPLATES[bundle]) {
    return [];
  }

  const detailLevel = bundle;
  return SSO_DETAIL_TEMPLATES[detailLevel].map(item => ({
    label: item.label,
    value: account[item.field],
  }));
};

/**
 * Normalizes a raw SSO account list into table-ready rows.
 *
 * @param {Object[]} accounts Raw account rows from the API.
 * @returns {Object[]} Normalized account rows with display-friendly values.
 */
export const normalizeSSOAccounts = accounts =>
  accounts.map((account, index) => ({
    ...account,
    id: account.id || account.name || `sso_account_${index}`,
    role: getRoleDisplayLabel(account),
    office: getOfficeCompanyDisplay(account),
    user_id: getCellDisplay(account.user_id),
    account_code: getCellDisplay(account.account_code),
    efin: getCellDisplay(account.efin),
    last_used: getLastUsedDisplay(account),
  }));

/**
 * Extracts the SSO pre-auth token from the URL hash fragment.
 *
 * @param {string} hash URL hash fragment.
 * @returns {string|null} Encoded pre-auth token, or null when absent.
 */
export const extractSSOPreAuthTokenFromHash = hash => {
  const match = hash.match(/[#&]sso_token=([^&]+)/);
  return match ? match[1] : null;
};

/**
 * Returns the no-account headline, subtext, and raw reason code based on the
 * `reason` query param set by the backend redirect (auth/sso.go).
 *
 * @param {{search: string}} params UI context inputs.
 * @returns {{mainText: string, subText: string, reasonCode: string|null}} Copy for the no-account page.
 */
export const getSSONoAccountCopy = ({ search }) => {
  const reason = new URLSearchParams(search).get('reason');

  if (reason === SSO_MFA_REASON) {
    return {
      mainText: 'Multi-factor authentication required',
      subText:
        'Your identity provider requires multi-factor authentication to access this application. Please sign in again and complete the MFA step, then retry.',
      reasonCode: reason,
    };
  }

  const copy = (reason && SSO_NO_ACCOUNT_COPY_BY_REASON[reason]) || SSO_NO_ACCOUNT_DEFAULT_COPY;

  return { ...copy, reasonCode: reason || null };
};

/**
 * Renders fixed column widths for the SSO picker table layout.
 *
 * @returns {JSX.Element} Table colgroup element.
 */
export const renderSSOAccountPickerColumnGroup = () => (
  <colgroup>
    <col style={{ width: '15%' }} />
    <col style={{ width: '8%' }} />
    <col style={{ width: '21%' }} />
    <col style={{ width: '10%' }} />
    <col style={{ width: '12%' }} />
    <col style={{ width: '9%' }} />
    <col style={{ width: '11%' }} />
    <col style={{ width: '14%' }} />
  </colgroup>
);

/**
 * Fail-closed expiry helper for the picker pre-auth session.
 * Invalidates stale async request guards, clears session artifacts, and routes
 * the user back to login with explicit expiry context.
 */
export const expirePickerSession = ({
  isPickerActiveRef,
  hasHandledSessionExpiryRef,
  latestRequestIDRef,
  latestLoginRequestIDRef,
  cancelLoginRequestRef,
  dispatch,
  history,
}) => {
  if (!isPickerActiveRef.current) {
    return false;
  }

  if (hasHandledSessionExpiryRef.current) {
    return false;
  }

  hasHandledSessionExpiryRef.current = true;

  // Invalidate any pending account-load/login continuations before redirect.
  latestRequestIDRef.current += 1;
  latestLoginRequestIDRef.current += 1;
  if (cancelLoginRequestRef.current) {
    cancelLoginRequestRef.current('SSO account login request was canceled');
    cancelLoginRequestRef.current = null;
  }

  sessionStorage.removeItem(SSO_PRE_AUTH_TOKEN_KEY);
  WebHelpers.cleanupSession();
  WebHelpers.cleanupLocalStorage();
  dispatch(appActions.onLogout());
  ErrorHelpers.handleError(
    'SSO Session Expired',
    ErrorHelpers.createSimpleError(
      'Your SSO session has expired. Please sign in again to continue.',
    ),
  );
  history.push({ pathname: '/' });

  return true;
};
